import type { Action, ListenerMiddleware, Reducer, StateFromReducersMapObject, ThunkDispatch } from '@reduxjs/toolkit';
import { configureStore } from '@reduxjs/toolkit';
import type { FC, PropsWithChildren, ReactElement } from 'react';
import type { TypedUseSelectorHook } from 'react-redux';
import { Provider, useDispatch, useSelector } from 'react-redux';

import { autoplaySyncMiddleware } from './middleware/autoplaySyncMiddleware';
import { bottomSheetMiddleware } from './middleware/bottomSheetMiddleware';
import { chipSelectionMiddleware } from './middleware/chipSelectionMiddleware';
import { chipsMiddleware } from './middleware/chipsMiddleware';
import { crossOriginCommunicationMiddleware } from './middleware/crossOriginCommunicationMiddleware';
import { createFetchErrorMiddleware } from './middleware/fetchErrorMiddleware';
import { localisationMiddleware } from './middleware/localisationMiddleware';
import { playerConfigMiddleware } from './middleware/playerConfigMiddleware';
import { settingSyncMiddleware } from './middleware/settingSyncMiddleware';
import { subtitlesSyncMiddleware } from './middleware/subtitlesSyncMiddleware';
import { userInfoMiddleware } from './middleware/userInfoMiddleware';
import { visibilityMiddleware } from './middleware/visibilityMiddleware';
import { autoplaySlice } from './slices/autoplay/slice';
import { betMiddleware } from './slices/bets/middleware';
import { betSlice } from './slices/bets/slice';
import { bonusPromotionsSlice } from './slices/bonusPromotions/slice';
import { chatSlice } from './slices/chat/slice';
import { chipSlice } from './slices/chips/slice';
import { configSlice } from './slices/config/slice';
import { gameStateSlice } from './slices/game/slice';
import { headerSlice } from './slices/header/slice';
import { initializationSlice } from './slices/initialization/slice';
import { miniLobbySlice } from './slices/minilobby/slice';
import { nickNameInfoSlice } from './slices/nickname/slice';
import { performanceSlice } from './slices/performance/slice';
import { popupMiddleware } from './slices/popup/middleware';
import { popupSlice } from './slices/popup/slice';
import { promotionsV2Slice } from './slices/promotionsV2/slice';
import { settingsSlice } from './slices/settings/slice';
import { subtitlesSlice } from './slices/subtitles/slice';
import { toastSlice } from './slices/toast/slice';
import { videoSlice } from './slices/video/slice';

export const coreReducer = {
  [autoplaySlice.name]: autoplaySlice.reducer,
  [betSlice.name]: betSlice.reducer,
  [chatSlice.name]: chatSlice.reducer,
  [chipSlice.name]: chipSlice.reducer,
  [configSlice.name]: configSlice.reducer,
  [gameStateSlice.name]: gameStateSlice.reducer,
  [headerSlice.name]: headerSlice.reducer,
  [initializationSlice.name]: initializationSlice.reducer,
  [popupSlice.name]: popupSlice.reducer,
  [promotionsV2Slice.name]: promotionsV2Slice.reducer,
  [bonusPromotionsSlice.name]: bonusPromotionsSlice.reducer,
  [settingsSlice.name]: settingsSlice.reducer,
  [toastSlice.name]: toastSlice.reducer,
  [performanceSlice.name]: performanceSlice.reducer,
  [subtitlesSlice.name]: subtitlesSlice.reducer,
  [nickNameInfoSlice.name]: nickNameInfoSlice.reducer,
  [miniLobbySlice.name]: miniLobbySlice.reducer,
  [videoSlice.name]: videoSlice.reducer,
};

const fetchErrorMiddleware = createFetchErrorMiddleware();

export const createGameStore = <GameReducer extends Record<string, Reducer>>(
  reducer: GameReducer,
  options?: { middleware: ListenerMiddleware<unknown, ThunkDispatch<unknown, unknown, Action>, unknown>[] }
) => {
  return configureStore({
    reducer: Object.assign({}, coreReducer, reducer),
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware()
        .concat(betMiddleware.middleware)
        .concat(chipSelectionMiddleware.middleware)
        .concat(chipsMiddleware.middleware)
        .concat(crossOriginCommunicationMiddleware.middleware)
        .concat(fetchErrorMiddleware.middleware)
        .concat(localisationMiddleware.middleware)
        .concat(playerConfigMiddleware.middleware)
        .concat(settingSyncMiddleware.middleware)
        .concat(subtitlesSyncMiddleware.middleware)
        .concat(autoplaySyncMiddleware.middleware)
        .concat(bottomSheetMiddleware.middleware)
        .concat(popupMiddleware.middleware)
        .concat(visibilityMiddleware.middleware)
        .concat(userInfoMiddleware.middleware)
        .concat([...(options?.middleware || [])]),
    devTools: {
      predicate: (_, action) =>
        !['chat/storeGameChatMessage', 'chat/storeLastChatMessage', 'settings/setStreamLatency'].includes(action.type),
    },
  });
};

export interface CoreRootState extends StateFromReducersMapObject<typeof coreReducer> {}
export const useCoreSelector: TypedUseSelectorHook<CoreRootState> = useSelector;

export interface CoreStoreType extends ReturnType<typeof createGameStore> {}
export type CoreDispatch = CoreStoreType['dispatch'];
export const useCoreDispatch: () => CoreDispatch = useDispatch<CoreDispatch>;

export interface CoreThunkConfig {
  dispatch: CoreDispatch;
  state: CoreRootState;
}
export interface CoreStoreProviderProps extends PropsWithChildren {
  store: CoreStoreType;
}

export const CoreStoreProvider: FC<CoreStoreProviderProps> = ({ children, store }): ReactElement => (
  <Provider store={store}>{children}</Provider>
);
